import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, ApplicationUser, ApplicationUserViewModel, IApplicationConfiguration } from '../../../../models';
import { Roles, wtDroppableDrop } from '../../../../common/';
import { IApplicationService, IAuthorizationService, IapplicationUserService, ICurrentApplicationService } from '../../../../services';
import { StateControllerBase } from '../../../StateControllerBase';

interface IapplicationUsersForm extends angular.IFormController {
    $visible: boolean;
}

interface IapplicationUserViewModel extends ApplicationUser {
    roleProperties: any;
    guid: any;
    viewModel: ApplicationUserViewModel;
}

class ApplicationRolesStateController extends StateControllerBase implements angular.IComponentController {
    applicationUsers: IapplicationUserViewModel[];
    unusedUsers: ApplicationUserViewModel[];
    roles = [Roles.administrator, Roles.master, Roles.engineer, Roles.operator, Roles.reader];
    isLoading: boolean;
    loadingMessage: string;
    applicationUsersForm: IapplicationUsersForm;
    errors: any[];
    actions: { icon: string; callback: (vm: ApplicationUserViewModel) => void }[];
    private _actions: { icon: string; callback: (vm: ApplicationUserViewModel) => void }[];
    private _application: IApplicationConfiguration;
    private _addedUsers: any[];

    static $inject = ['$scope', '$q', '$translate', 'applicationService',
        'authorizationService', 'applicationUserService', 'currentApplicationService'];

    constructor(private _$scope: angular.IScope,
        private _$q: angular.IQService,
        private _$translate: angular.translate.ITranslateService,
        private applicationService: IApplicationService,
        authorizationService: IAuthorizationService,
        private _applicationUserService: IapplicationUserService,
        private _currentApplicationService: ICurrentApplicationService) {
        super(authorizationService);
    }


    $onInit() {
        this.applicationUsers = [];
        this.unusedUsers = [];
        this.errors = [];
        this._addedUsers = [];
        this._actions = [{ icon: 'fa-plus', callback: user => this.addUser(user) }];
        this._application = this._currentApplicationService.getApplicationConfiguration();
        this.loadUsers();
        this._$scope.$on(wtDroppableDrop, (event, data) => {
            if (this.applicationUsersForm && this.applicationUsersForm.$visible) {
                const user = _.find(this.unusedUsers, u => u.guid === data.itemId);
                if (user) {
                    this.addUser(user);
                }
            }
        });
    }

    private loadUsers(force: boolean = false) {
        this.loadingMessage = 'Loading users';
        this.isLoading = true;
        this._applicationUserService.getAllUsersWithRoles(this._application.guid, force)
            .then(data => {
                this.applicationUsers = data.filter(d => d.roles.length > 0)
                    .sort((a, b) => a.displayName > b.displayName ? 1 : -1)
                    .map(a => this.toLocalViewModel(a));
                this.unusedUsers = data.filter(d => d.roles.length <= 0)
                    .sort((a, b) => a.displayName > b.displayName ? 1 : -1);
            })
            .finally(() => this.isLoading = false);
    }

    private isInRole(user: { roles: string[] }, role: string) {
        return user.roles.indexOf(role) > -1;
    }

    addUser(user: ApplicationUserViewModel) {
        const applicationUsers: IapplicationUserViewModel = this.toLocalViewModel(user);
        applicationUsers.roles = [Roles.reader];
        applicationUsers.roleProperties[Roles.reader] = true;
        this.applicationUsers.push(applicationUsers);
        this._addedUsers.push(applicationUsers);
        this.applicationUsersForm.$setDirty();
        _.remove(this.unusedUsers, u => u.guid === user.guid);
    }

    setRole(user, role) {
        if (user[role]) {
            user.roles.push(role);
        }
    }

    save() {
        this.loadingMessage = 'Saving users';
        this.errors = [];
        this.applicationUsers.forEach(au => {
            _.remove(au.roles, () => true);
            for (const key of Object.keys(au.roleProperties)) {
                if (au.roleProperties[key]) {
                    au.roles.push(key);
                }
            }
        });
        let promise: angular.IPromise<any>;
        if (_.every(this.applicationUsers, u => !this.isInRole(u, Roles.administrator))) {
            const message = this._$translate.instant('At least one user has to be administrator of an application.');
            this.errors.push(message);
            promise = this._$q.reject(message);
            // defered.reject(message);
        } else {
            this.isLoading = true;
            promise = this.applicationService.updateUsers(this._application.guid, this.applicationUsers)
                .then(data => {
                    this.applicationUsersForm.$setPristine();
                    this.actions = [];
                    return this.loadUsers(true);
                })
                .catch((reason) => {
                    this.errors.push(reason);
                    throw reason;
                });
        }
        return promise;
    }

    cancel() {
        this.loadUsers();
        this._addedUsers = [];
        this.applicationUsersForm.$setPristine();
        this.actions = [];
        this.errors = [];
    }

    showForm() {
        this.actions = this._actions;
        this.applicationUsersForm.$show();
    }

    private toLocalViewModel(a: ApplicationUserViewModel): IapplicationUserViewModel {
        const roleProperties = {};
        this.roles.forEach(r => {
            roleProperties[r] = this.isInRole({ roles: a.roles || [] }, r);
        });
        return {
            roleProperties,
            guid: a.userId,
            viewModel: a,
            name: a.displayName,
            roles: a.roles,
            userId: a.userId
        };
    }
}


export const wtApplicationRolesState: ComponentDefinition = {
    componentId: 'wtApplicationRolesState',
    controller: ApplicationRolesStateController,
    template: require('./applicationRolesState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};
